import castArray from 'lodash/castArray'
import { Entry, Freer, LifeCycles, Rebuilder, RegistrableApp } from 'qiankun'
import hijacker from './dynamicStyleAppend'
import { Subject } from 'rxjs'
import { RUNTIME_ENV } from '@/constants/environment'
import { initSentry } from '@/utils/sentry'
import { shellConfig } from './shell-config'
import {
  sourceFetch,
  createRepeatFetch,
  getBuildInfo,
  reportRetry,
  urlJoin,
  CAN_TO_CND_FOR_WEBPACK_JSON,
  addRetrySentryBreadcrumb,
} from './utils/retry-fetch'

export type Assets = {
  app: {
    js: string[]
    css: string[]
  }
  metadata: {
    build: {
      projectName: string
      shortCommit: string
      longCommit: string
      branch: string
      buildTime: string
    }
    sentryRelease: string
  }
  integrities?: Record<string, string>
  manifest?: Array<{ url: string; revision: string }>
}

const nonWebpackApps: Record<string, any> = {
  // SUB_APP__TALK: 'talk',
}

const vite2webpackApps: Record<
  string,
  {
    mountId: string
    prefixClass: string
    withCommonQiankun: boolean
    microApp: string // 影响样式
  }
> = {
  SUB_APP__TALK: {
    mountId: 'talk-app',
    prefixClass: '',
    withCommonQiankun: true,
    microApp: 'okki-vite-project',
  },
}

const getDefaultAssets = (): any => ({
  app: { js: [], css: [] },
  metadata: {
    build: {},
  },
  integrities: {},
  manifest: [],
})

const getAppAssets = (url: string, item: AppConfig): Promise<Assets> => {
  const mainDomainSource = urlJoin(url.startsWith('//') ? '' : location.origin, url, `webpack-assets.json`)

  const buildInfo = getBuildInfo()
  const commitId = buildInfo?.longCommit?.slice(0, 10) || ''
  const buildTimeStamp = new Date(buildInfo?.buildTime?.replace(/\-/g, '/') || '').getTime()
  const cdnSource =
    commitId && CAN_TO_CND_FOR_WEBPACK_JSON.includes(item.assetsBaseURL)
      ? urlJoin(item.assetsBaseURL, `webpack-assets-${commitId}.json?t=${buildTimeStamp}`)
      : ''

  const fetch = createRepeatFetch(sourceFetch, {
    max: 3,
    onRepeat(args, curCount) {
      if (curCount === 3 && cdnSource) {
        addRetrySentryBreadcrumb('json', {
          url: args[0] as string,
          target: cdnSource,
          curCount,
        })
        return [cdnSource, args[1]]
      }
      addRetrySentryBreadcrumb('json', {
        url: args[0] as string,
        curCount,
      })
      return args
    },
    onFinally({ isSuccess, count, messages, finallyUrl }) {
      if (count === 1 && isSuccess) {
        return
      }
      const originUrl = mainDomainSource
      if (originUrl === finallyUrl) {
        reportRetry(isSuccess ? 'retry-json-repeat-success' : 'retry-json-repeat-fail', {
          url: originUrl,
          messages,
          count,
        })
      } else {
        reportRetry(isSuccess ? 'retry-json-last-success' : 'retry-json-last-fail', {
          url: originUrl,
          target: finallyUrl,
          messages,
          count,
        })
      }
    },
  })
  return fetch(mainDomainSource, {
    mode: 'cors',
    credentials: 'include',
  })
    .then((res) => res.json())
    .catch(() => getDefaultAssets())
}

export const getAppEntry = ({ js = [], css = [] }: Assets['app'], name: string) => {
  return {
    scripts: castArray(js).map((path) => `${path}`),
    styles: castArray(css).map((path) => `${path}`),
    html: `<div id="${vite2webpackApps[name]?.mountId ?? 'app'}" class="${
      vite2webpackApps[name]?.prefixClass
    }" micro-app="${vite2webpackApps[name]?.microApp}"></div>
    ${vite2webpackApps[name]?.withCommonQiankun ? '<div id="common-qiankun"></div>' : ''}`,
  }
}

export type AppConfig = Omit<RegistrableApp, 'render' | 'entry'> & {
  assetsURL: string
  assetsBaseURL: string
  routeBase?: string
  precache: 'entry' | 'all' | 'none'
}

export type AppAssetsConfig = AppConfig & { assets: Assets }

export type AppProps = {
  pageUserSubject: Subject<any>
  config: AppAssetsConfig
  precache: () => Promise<any>
}

const appendBaseUrl = (baseUrl: string, url: string) => {
  if (url.startsWith('http')) {
    return url
  }
  return urlJoin(baseUrl, url)
}

export const getSubAppsConfig = (): Promise<AppAssetsConfig[]> => {
  const apps = shellConfig.subApps

  // 给资源补上对应的 bsae_url 前缀
  const patchBaseURL = (assetsConfig: AppAssetsConfig) => {
    const { assets, assetsBaseURL } = assetsConfig
    const newAssets = {
      ...assets,
      app: {
        js: assets.app.js.map((url) => appendBaseUrl(assetsBaseURL, url)),
        css: assets.app.css?.map((url) => appendBaseUrl(assetsBaseURL, url)),
      },
      manifest: assets.manifest?.map((item) => ({
        ...item,
        url: appendBaseUrl(assetsBaseURL, item.url),
      })),
    }

    return {
      ...assetsConfig,
      assets: newAssets,
    }
  }
  return Promise.all(
    apps.map((item) => {
      if (nonWebpackApps[item.name]) {
        const assets = getDefaultAssets()
        // galio那块的项目没法塞metadata的信息，考虑到shell将重构 先粗暴点处理
        assets.metadata.build['projectName'] = nonWebpackApps[item.name]
        return patchBaseURL({
          ...item,
          assets,
        })
      }

      return getAppAssets(item.assetsURL, item).then((assets) =>
        patchBaseURL({
          ...item,
          assets,
        }),
      )
    }),
  )
}

export const getLifeCycles = (masterAppMounted: Promise<unknown>) => {
  const appsFreer = new Map<string, Freer>()
  const appsSideEffectsRebuild = new Map<string, Rebuilder>()

  const lifeCycles: LifeCycles<AppProps> = {
    beforeLoad: async (app) => {
      appsFreer.set(
        app.name,
        hijacker(app.name, [(el) => el.href?.startsWith('https://cfile.xiaoman.cn/v5/res/web/libs/')]),
      )
      ;(app.entry as Entry & object).styles?.forEach((href) => {
        const link = document.createElement('link')
        link.setAttribute('href', href)
        link.setAttribute('rel', 'stylesheet')
        document.head.appendChild(link)
      })
    },
    beforeMount: [
      () => masterAppMounted,
      async (app) => {
        const qiankunWindow = window.proxy || window
        qiankunWindow.__webpack_public_path__ = app.props!.config.assetsBaseURL
        qiankunWindow.__QIANKUN_ROUTE_PATH__ = app.props!.config.routeBase

        if (app.props!.config.precache === 'entry') {
          await app.props!.precache().then(() => {
            console.info('precache done')
          })
          console.info('precache end')
        }
      },
      async (app) => {
        const rebuild = appsSideEffectsRebuild.get(app.name)
        if (rebuild) {
          await rebuild()
        }
      },
      async (app) => {
        if (['production', 'omg'].includes(RUNTIME_ENV)) {
          const { build, sentryRelease } = app.props!.config.assets.metadata
          const { projectName } = build
          initSentry(projectName, sentryRelease)
        }
      },
    ],
    afterMount: async (app) => {
      // bootstrap precache after mounted 2s
      if (app.props!.config.precache === 'all') {
        setTimeout(() => {
          app.props!.precache()
        }, 2000)
      }
      window.performance.mark('vital_mark_subApp')
      try {
        window.performance.measure('start2shell', 'vital_mark_start', 'vital_mark_shell')
        window.performance.measure('shell2subApp', 'vital_mark_shell', 'vital_mark_subApp')
      } catch (error) {}
    },
    afterUnmount: async (app) => {
      const freer = appsFreer.get(app.name)
      if (freer) {
        appsSideEffectsRebuild.set(app.name, freer())
      }
    },
  }

  return lifeCycles
}
